import { get } from "@ember/object"
import Service, { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"
import type { GoogleClass, GoogleClassTopic } from "@shared/models/google-classroom"
import type { Session } from "@shared/models/session"
import type { User } from "@shared/models/user"
import ObjectId from "@shared/util/object-id-gen"
import ENV from "../config/environment"
import type { definitions } from "./rpc-types"

type RpcHTTPResponse<T, E> = RpcHTTPSuccessResponse<T> | RpcHTTPErrorResponse<E>
export type RpcResponse<T, E = Error> = RpcError<E> | RpcSuccess<T>

type Result<T> = T

interface Error {
  status: number
  code: string
  message: string
  data?: object
}

// ERROR

interface RpcHTTPErrorResponse<E = Error> {
  id: string
  jsonrpc: "2.0"
  error: E
}

interface RpcError<T = Error> {
  responseOk: false
  result: T
}

// SUCCESS

interface RpcHTTPSuccessResponse<T> {
  id: string
  jsonrpc: "2.0"
  result: Result<T>
}

interface RpcSuccess<T> {
  responseOk: true
  result: T
}

export type group_add_individual_rpc_response = RpcResponse<
  { ok: true; message: "SUCCESS" },
  { code: "USER_ALREADY_ADMIN" | "USER_ALREADY_MEMBER"; message: string; status: number }
>

function log(target, name, descriptor) {
  const original = descriptor.value
  if (typeof original === "function") {
    descriptor.value = function (...args) {
      Sentry.addBreadcrumb({
        message: name,
        category: "rpc",
      })
      return original.apply(this, args)
    }
  }
  return descriptor
}

// We have this decorator so we don't have to keep typing the name of the
// rpc method. We also can do some nice logging.
function rpc<T, E = Error>(_target, name, descriptor) {
  descriptor.value = function (params): Promise<RpcResponse<T, E>> {
    let id = ObjectId.create()
    Sentry.addBreadcrumb({
      data: { rpcId: id },
      message: name,
      category: "rpc",
    })
    return this.ajax
      .post(`${ENV.API_ROOT}/rpc/${name}`, {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          id: id,
          params: params,
        }),
      })
      .then((data: RpcHTTPResponse<T, E>): RpcResponse<T, E> => {
        if ("error" in data) {
          Sentry.withScope((scope) => {
            let e = new Error(`RPC_ERROR_${name}`)
            scope.setFingerprint([name])
            scope.setTag("rpcName", name)
            scope.setTag("rpcId", id)
            Sentry.captureException(e)
          })
          return { responseOk: false, result: data.error }
        } else {
          return { responseOk: true, result: data.result }
        }
      })
  }

  return descriptor
}

export default class Rpc extends Service {
  @service ajax

  @service finder

  @service socket

  // SHOULD ADD THE TOKEN FROM THE SESSION
  // @deprecated - Use the @log decorator and call the method directly
  execute(type: string, ...payload) {
    Sentry.addBreadcrumb({
      message: type,
      category: "rpc",
    })
    return this[type](...payload)
  }

  postToRpcEndpoint(method, params): Promise<{ ok: false }> | Promise<{ data: object }> {
    let id = ObjectId.create()
    Sentry.addBreadcrumb({
      data: { rpcId: id },
      message: method,
      category: "rpc",
    })
    Sentry.setTag("rpcId", id)
    return get(this, "ajax")
      .post(`${ENV.API_ROOT}/rpc/${method}`, {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          id: id,
          params: params,
        }),
      })
      .then((data) => {
        if (data.error) {
          return Object.assign({ ok: false }, data.error)
        } else {
          return data.result
        }
      })
      .catch((data) => {
        return Object.assign({ ok: false })
      })
  }

  /**
   * Queries
   */

  FETCH_QUERY(queryName, id, params) {
    return get(this, "ajax")
      .request(
        `${ENV.API_ROOT}/query/${queryName}/${id}?limit=${params.limit}&offset=${params.offset}`,
        {
          dataType: "json",
          contentType: "application/json; charset=utf-8",
          params: params,
        }
      )
      .then((data) => {
        return data
      })
  }

  /**
   * ====================
   * USER
   * ====================
   */
  USER_CHECK_FOR_ACCOUNT(email) {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/users/check_email", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          email: email,
        }),
      })
      .then((data) => {
        return data
      })
  }

  SEND_RESET_PASSWORD_EMAIL(email, password) {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/users/send_reset_password", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          email: email,
        }),
      })
      .then((data) => {
        return data
      })
  }

  EXCHANGE_TOKEN(sessionId, token) {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/sessions/exchange_token", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          id: sessionId,
          token: token,
        }),
      })
      .then((data) => {
        return data
      })
  }

  GET_PRINT_TOKEN() {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/sessions/print_token", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
      })
      .then((data) => {
        return data.token
      })
  }

  CHANGE_PASSWORD(password) {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/users/change_password", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          password: password,
        }),
      })
      .then((data) => {
        return data
      })
  }

  CHANGE_EMAIL(email) {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/users/change_email", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          email: email,
        }),
      })
      .then((data) => {
        return data
      })
  }

  INVITE_TO_CC({ emails: emails }) {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/users/invite_to_cc", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          emails: emails,
        }),
      })
      .then((data) => {
        return data
      })
  }

  SIGN_OUT() {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/users/sign_out", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
      })
      .then((data) => {
        return data
      })
  }

  AUTHENTICATE_WITH_TOKEN(token) {
    return this.socket.authenticateWithToken(token)
  }

  @rpc session_authenticate_with_email_and_password(
    params: definitions["session_authenticate_with_email_and_password"]["params"]
  ): Promise<RpcResponse<{ data: Session; included: [User] }>> {
    // @ts-ignore
    return
  }

  USER_CREATE_ACCOUNT(sessionId, email, password, promotionCode) {
    return get(this, "ajax")
      .postRaw(ENV.API_ROOT + "/users", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          sessionId: sessionId,
          email: email,
          password: password,
          promotionCode: promotionCode,
        }),
      })
      .then((data) => {
        return data
      })
  }

  IMPERSONATE_BY_USER_ID({ userId }) {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/sessions/impersonate", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          userId: userId,
        }),
      })
      .then((data) => {
        return data
      })
      .catch((error) => console.error(error))
  }

  IMPERSONATE_BY_EMAIL({ email }) {
    return get(this, "ajax")
      .post(ENV.API_ROOT + "/sessions/impersonate", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          email: email,
        }),
      })
      .then((data) => {
        return data
      })
      .catch((error) => console.error(error))
  }

  FETCH_SESSION_ACTIONS(sessionId) {
    return get(this, "ajax")
      .request(`${ENV.API_ROOT}/actions?sessionId=${sessionId}`)
      .then((data) => data.data)
      .catch((error) => {
        console.error(error)
      })
  }

  FETCH_USER_ACTIONS(userId) {
    return get(this, "ajax")
      .request(`${ENV.API_ROOT}/actions?userId=${userId}`)
      .then((data) => data.data)
      .catch((error) => {
        console.error(error)
      })
  }

  FETCH_USER_SESSIONS(userId) {
    return get(this, "ajax")
      .request(`${ENV.API_ROOT}/sessions?userId=${userId}`)
      .then((data) => data.data)
      .catch((error) => {
        console.error(error)
      })
  }

  SESSION_NOTIFY_FAILED_ACTIONS({ sessionId }) {
    return get(this, "ajax")
      .post(`${ENV.API_ROOT}/sessions/${sessionId}/notify_failed_actions`)
      .then((data) => data.data)
      .catch((error) => {
        console.error(error)
      })
  }

  /**
   * =====================
   * GROUP
   * =====================
   */

  @rpc group_add_individual(
    _params: definitions["group_add_individual"]["params"]
  ): Promise<group_add_individual_rpc_response> {
    // @ts-ignore
    return
  }

  @rpc group_remove_individual(
    _params: definitions["group_remove_individual"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc group_request_membership(
    _params: definitions["group_request_membership"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc group_approve_membership_request(
    _params: definitions["group_approve_membership_request"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc group_ignore_other_membership(
    _params: definitions["group_ignore_other_membership"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc group_cancel_other_membership(
    _params: definitions["group_cancel_other_membership"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  /**
   * =====================
   * PLANBOOKS
   * =====================
   */

  PLANBOOK_SEARCH(query, planbookId) {
    return get(this, "ajax").request(
      `${ENV.API_ROOT}/planbooks/${planbookId}/search?query=${encodeURI(query)}`,
      {
        method: "GET",
        contentType: "application/json; charset=utf-8",
      }
    )
  }

  PLANBOOK_DELETE(id) {
    return get(this, "ajax").del(`${ENV.API_ROOT}/planbooks/${id}`)
  }

  PLANBOOK_CLEAN_UP_DELETED_COURSES({ planbookId }) {
    return get(this, "ajax").request(
      `${ENV.API_ROOT}/planbooks/${planbookId}?cleanUpDeletedCourses=true`
    )
  }

  STANDARDS_REPORT(courseIds, standardSetIds) {
    return get(this, "ajax")
      .request(
        ENV.API_ROOT +
          `/standards_report?courseIds=${courseIds.join(",")}&standardSetIds=${standardSetIds.join(
            ","
          )}`
      )
      .then((data) => {
        return data.data
      })
  }

  STANDARDS_REPORT_FIND_STANDARD_SET_IDS(courseIds) {
    return get(this, "ajax").request(
      ENV.API_ROOT + `/standards_report?courseIds=${courseIds.join(",")}&findStandardSetIds=true`
    )
  }

  //====================================================================================================
  // Invite Accept
  //====================================================================================================

  @rpc invite_accept(
    params: definitions["invite_accept"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  //====================================================================================================
  // Collaborators
  //====================================================================================================

  @rpc planbook_collaboration_add_individual(
    params: definitions["planbook_collaboration_add_individual"]["params"]
  ): Promise<RpcResponse<{ ok: boolean; name?: string; message?: string }>> {
    // @ts-ignore
    return
  }
  @rpc planbook_collaboration_change_collaborator_role(
    params: definitions["planbook_collaboration_change_collaborator_role"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc planbook_collaboration_remove_collaborator(
    params: definitions["planbook_collaboration_remove_collaborator"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }
  @rpc planbook_collaboration_add_group(
    params: definitions["planbook_collaboration_add_group"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }
  @rpc planbook_collaboration_change_public_permission(
    params: definitions["planbook_collaboration_change_public_permission"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  /**
   * =====================
   * COURSE
   * =====================
   */

  COURSE_CREATE(planbookId: string, isNonInstructional: boolean) {
    return get(this, "ajax").request(ENV.API_ROOT + "/courses", {
      method: "POST",
      dataType: "json",
      contentType: "application/json; charset=utf-8",
      body: JSON.stringify({
        planbook_id: planbookId,
        is_non_instructional: isNonInstructional,
      }),
    })
  }

  COURSE_DELETE(courseId) {
    return get(this, "ajax").del(ENV.API_ROOT + "/courses/" + courseId)
  }

  COURSE_CHANGE_CARD_STACK_CARD_IDS(id) {
    return get(this, "ajax").request(ENV.API_ROOT + `/courses/${id}?updateCardStackCardIds=true`)
  }

  COURSE_SEARCH(query, courseId) {
    return get(this, "ajax").request(
      `${ENV.API_ROOT}/courses/${courseId}/search?query=${encodeURI(query)}`,
      {
        method: "GET",
        contentType: "application/json; charset=utf-8",
      }
    )
  }

  /**
   * =====================
   * CLASS WEBSITES
   * =====================
   */

  CLASS_WEBSITE_UPDATE_SLUG(id: string, slug: string) {
    return get(this, "ajax").post(`${ENV.API_ROOT}/class_websites/${id}/set_slug/${slug}`)
  }

  /**
   * Card Stack
   */

  CARD_STACK_COPY_TRANSFERRABLE_ARRAY({
    transferrableArray,
    newCourseId,
    originalPlanbookId,
    originalOwnerIds,
    originalCourseId,
  }) {
    return get(this, "ajax").post(ENV.API_ROOT + "/card_stacks/copy_transferrable_array", {
      dataType: "json",
      contentType: "application/json; charset=utf-8",
      body: JSON.stringify({
        transferrableArray,
        newCourseId,
        originalPlanbookId,
        originalOwnerIds,
        originalCourseId,
      }),
    })
  }

  /**
   * Document Versions
   */

  GET_DOCUMENT_VERSIONS_FOR(modelType, id) {
    return get(this, "ajax").request(
      `${ENV.API_ROOT}/document_versions?modelType=${modelType}&id=${id}`
    )
  }

  GET_CARD_STACK_VERSIONS(id) {
    return get(this, "ajax").request(`${ENV.API_ROOT}/card_stack_versions?id=${id}`)
  }

  ROLLBACK(id) {
    return get(this, "ajax").post(`${ENV.API_ROOT}/document_versions/${id}/rollback`)
  }

  ROLLBACK_CARD_STACK({ cardStackId, version }) {
    return get(this, "ajax").post(
      `${ENV.API_ROOT}/card_stack_versions/${cardStackId}/rollback/${version}`
    )
  }

  /**
   * Admin
   */

  // ADMIN_GROUP_CREATE(params) {
  //   return this.postToRpcEndpoint("group_admin_create", params)
  // },
  //
  ADMIN_GROUP_ADD_CHILD(params) {
    return this.postToRpcEndpoint("group.admin-add-child-group", params)
  }

  ADMIN_GROUP_REMOVE_CHILD(params) {
    return this.postToRpcEndpoint("group.admin-remove-child-group", params)
  }

  /**
   * School Year
   */

  ADMIN_SCHOOL_YEAR_CREATE(params) {
    return this.postToRpcEndpoint("schoolyear_admin_create", params)
  }

  @log
  ADMIN_SCHOOL_YEAR_INDEX(params) {
    return get(this, "ajax").request(`${ENV.API_ROOT}/school_years`)
  }

  @log
  ADMIN_HOLIDAY_INDEX() {
    return get(this, "ajax").request(`${ENV.API_ROOT}/holidays`)
  }

  @rpc holiday_admin_create(
    params: definitions["holiday_admin_create"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @log
  holidays_for_school_year(params: { schoolYearId: string }) {
    return get(this, "ajax").request(`${ENV.API_ROOT}/holidays?schoolYearId=${params.schoolYearId}`)
  }

  /**
   * State Transitions
   */

  ADMIN_GROUP_PAY_WITH_QUOTE(params) {
    return this.postToRpcEndpoint("group.pay-with-quote", params)
  }

  /**
   * Groups with new RPC
   */

  @rpc group_set_member_role(
    _params: definitions["group_set_member_role"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc fiscalgroup_set_member_upgrade_status(
    _params: definitions["fiscalgroup_set_member_upgrade_status"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc group_delete_child_group(
    _params: definitions["group_delete_child_group"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  group_create_school(params) {
    return this.postToRpcEndpoint("group_create_school", params)
  }

  @rpc group_create_school_v2(
    _params: definitions["group_create_school_v2"]["params"]
  ): Promise<RpcResponse<{ ok: boolean; groupId: string }>> {
    //@ts-ignore
    return
  }

  @rpc group_create_school_in_network(
    _params: definitions["group_create_school_in_network"]["params"]
  ): Promise<RpcResponse<{ newGroupId: string; ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc group_create_team(
    _params: definitions["group_create_team"]["params"]
  ): Promise<RpcResponse<{ newGroupId: string }>> {
    // @ts-ignore
    return
  }

  @rpc group_unlock(
    _params: definitions["group_unlock"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc group_request_unlock_teacher(
    _params: definitions["group_request_unlock_teacher"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc
  planbook_create_v2(
    params: definitions["planbook_create_v2"]["params"]
  ): Promise<RpcResponse<{ id: string }>> {
    // @ts-ignore
    return
  }

  planbook_transfer_to_new_owner(params) {
    return this.postToRpcEndpoint("planbook_transfer_to_new_owner", params)
  }

  //====================================================================================================
  // Users
  //====================================================================================================

  @rpc user_create(
    _params: definitions["user_create"]["params"]
  ): Promise<RpcResponse<{ data: User; included: [Session] }>> {
    // @ts-ignore
    return
  }

  user_delete(params) {
    return this.postToRpcEndpoint("user_delete", params)
  }

  user_change_product(params) {
    return this.postToRpcEndpoint("user_change_product", params)
  }

  @rpc user_upgrade_with_card(
    _params: definitions["user_upgrade_with_card"]["params"]
  ): Promise<RpcResponse<{ userId: string }, { contactSupport: boolean; message: string }>> {
    //@ts-ignore
    return
  }

  @rpc user_apply_coupon(
    _params: definitions["user_apply_coupon"]["params"]
  ): Promise<RpcResponse<{ userId: string }, { contactSupport: boolean; message: string }>> {
    //@ts-ignore
    return
  }

  user_cancel_subscription_from_app(params) {
    return this.postToRpcEndpoint("user_cancel_subscription_from_app", params)
  }

  @rpc fiscalgroup_admin_change_group_state(
    _params: definitions["fiscalgroup_admin_change_group_state"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    //@ts-ignore
    return
  }

  @rpc user_admin_create_graphql_key(
    _params: definitions["user_admin_create_graphql_key"]["params"]
  ): Promise<RpcResponse<{ token: string }>> {
    //@ts-ignore
    return
  }

  admin_send_in_app_message(params) {
    return this.postToRpcEndpoint("admin_send_in_app_message", params)
  }

  @rpc group_admin_create_fiscal_group(
    _params: definitions["group_admin_create_fiscal_group"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    //@ts-ignore
    return
  }

  planbookcopier_copy_planbook(params: definitions["planbookcopier_copy_planbook"]["params"]) {
    return this.postToRpcEndpoint("planbookcopier_copy_planbook", params)
  }
  planbookcopier_get_start_dates_from_parent_courses(
    params: definitions["planbookcopier_get_start_dates_from_parent_courses"]["params"]
  ) {
    return this.postToRpcEndpoint("planbookcopier_get_start_dates_from_parent_courses", params)
  }
  planbookcopier_import_lessons_from_parent_courses(
    params: definitions["planbookcopier_import_lessons_from_parent_courses"]["params"]
  ) {
    return this.postToRpcEndpoint("planbookcopier_import_lessons_from_parent_courses", params)
  }

  @rpc google_classroom_update_card(
    _params: definitions["google_classroom_update_card"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc planbook_admin_exact_copy(
    _params: definitions["planbook_admin_exact_copy"]["params"]
  ): Promise<RpcResponse<{ newPlanbookId: string }>> {
    // @ts-ignore
    return
  }

  @rpc course_admin_exact_copy(
    _params: definitions["course_admin_exact_copy"]["params"]
  ): Promise<RpcResponse<{ newCourseId: string }>> {
    // @ts-ignore
    return
  }

  /**
   *
   * @returns Behind the scenes, this hits the google API classroom courses/index route
   * and returns a list of googleCalssIDs and their title (colors, )
   */
  @rpc google_classroom_fetch_class_list(
    _params: definitions["google_classroom_fetch_class_list"]["params"]
  ): Promise<RpcResponse<{ ok: boolean; classes: Array<GoogleClass> }>> {
    // @ts-ignore
    return
  }
  /**
   *
   * @returns Behind the scenes, this hits the google API classroom courses/index route
   * and returns a list of googleCalssIDs and their title (colors, )
   */
  @rpc google_classroom_fetch_class_topics_list(
    _params: definitions["google_classroom_fetch_class_topics_list"]["params"]
  ): Promise<RpcResponse<{ ok: boolean; topics: Array<GoogleClassTopic> }>> {
    // @ts-ignore
    return
  }

  @rpc google_classroom_fetch_auth_for_integration(
    _params: definitions["google_classroom_fetch_auth_for_integration"]["params"]
  ): Promise<
    RpcResponse<{ ok: boolean; auth: { data: { attributes: { name: string; email: string } } } }>
  > {
    // @ts-ignore
    return
  }

  @rpc planbook_remove_integration(
    _params: definitions["planbook_remove_integration"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc planbook_migrate(
    _params: definitions["planbook_migrate"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc admin_csp_edit_set(
    _params: definitions["admin_csp_edit_set"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc admin_csp_duplicate_set(
    _params: definitions["admin_csp_duplicate_set"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc admin_csp_update_set(
    _params: definitions["admin_csp_update_set"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }

  @rpc admin_csp_create_jurisdiction(
    _params: definitions["admin_csp_create_jurisdiction"]["params"]
  ): Promise<RpcResponse<{ ok: boolean; id: string }>> {
    // @ts-ignore
    return
  }
  @rpc admin_csp_edit_jurisdiction(
    _params: definitions["admin_csp_edit_jurisdiction"]["params"]
  ): Promise<RpcResponse<{ ok: boolean }>> {
    // @ts-ignore
    return
  }
}
